import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, UserRound, Crown } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageWrap } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ConfirmDialog } from "../components/ConfirmDialog";

const UsersTab = ({ roles }) => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { username: form.username, name: form.name, email: form.email, role_id: form.role_id || "", active: form.active, password: form.password || null };
      if (form.id) await api.put(`/users/${form.id}`, body);
      else await api.post("/users", body);
      toast.success("Pengguna disimpan");
      setForm(null);
      load();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  const remove = async () => {
    try { await api.delete(`/users/${del.id}`); toast.success("Pengguna dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3">
        <p className="text-sm text-muted-foreground">Akun kasir & staf toko Anda. Username harus unik di seluruh platform.</p>
        <Button disabled={!roles.length} onClick={() => setForm({ username: "", name: "", email: "", role_id: roles[0]?.id, active: true, password: "" })} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" />Tambah Pengguna</Button>
      </div>
      <div className="rounded-2xl border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Username</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                <TableCell><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                <TableCell className="font-mono text-sm">{u.username}</TableCell>
                <TableCell>
                  <Badge variant={u.role === "owner" ? "default" : "secondary"} className="gap-1">
                    {u.role === "owner" ? <Crown className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}{u.role_name}
                  </Badge>
                </TableCell>
                <TableCell><Badge variant="outline" className={u.active ? "text-success" : "text-destructive"}>{u.active ? "Aktif" : "Nonaktif"}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...u, password: "" })} data-testid={`edit-user-${u.username}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-destructive" disabled={u.id === me.id || u.role === "owner"} onClick={() => setDel(u)} data-testid={`delete-user-${u.username}`}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Edit" : "Tambah"} Pengguna</DialogTitle>
            <DialogDescription>{form?.id ? "Kosongkan password jika tidak diubah." : "Password minimal 8 karakter, berisi huruf & angka."}</DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama</Label><Input required value={form.name} onChange={set("name")} data-testid="user-name-input" /></div>
              <div className="space-y-1.5"><Label>Username</Label><Input required disabled={!!form.id} value={form.username} onChange={set("username")} placeholder="cth: kasir1.tokoanda" data-testid="user-username-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email || ""} onChange={set("email")} data-testid="user-email-input" /></div>
              <div className="space-y-1.5"><Label>Password</Label><PasswordInput required={!form.id} value={form.password} onChange={set("password")} data-testid="user-password-input" /></div>
              {form.role !== "owner" && (
                <div className="flex items-center gap-4">
                  <Select value={form.role_id || ""} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                    <SelectTrigger className="w-44" data-testid="user-role-select"><SelectValue placeholder="Pilih role" /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" /><span className="text-sm">Aktif</span></div>
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="user-save-btn">Simpan</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus pengguna?" description={`Akun "${del?.username}" akan dihapus.`} onConfirm={remove} />
    </div>
  );
};

const RolesTab = ({ roles, perms, reload }) => {
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const label = Object.fromEntries(perms.map((p) => [p.key, p.label]));
  const toggle = (k, v) => setForm({ ...form, permissions: v ? [...form.permissions, k] : form.permissions.filter((x) => x !== k) });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { name: form.name, permissions: form.permissions };
      if (form.id) await api.put(`/roles/${form.id}`, body);
      else await api.post("/roles", body);
      toast.success("Role disimpan");
      setForm(null);
      reload();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  const remove = async () => {
    try { await api.delete(`/roles/${del.id}`); toast.success("Role dihapus"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3">
        <p className="text-sm text-muted-foreground">Atur hak akses setiap role sesuai kebutuhan toko. Owner selalu memiliki semua akses.</p>
        <Button onClick={() => setForm({ name: "", permissions: ["pos"] })} data-testid="add-role-btn"><Plus className="h-4 w-4 mr-2" />Tambah Role</Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.id} className="rounded-2xl border bg-card p-5 space-y-3" data-testid={`role-card-${r.name}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><ShieldCheck className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0"><div className="font-display font-bold">{r.name}</div><div className="text-xs text-muted-foreground">{r.user_count} pengguna</div></div>
              <Button size="icon" variant="ghost" onClick={() => setForm(r)} data-testid={`edit-role-${r.name}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDel(r)} data-testid={`delete-role-${r.name}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1.5">{r.permissions.map((p) => <Badge key={p} variant="outline" className="text-[11px]">{label[p] || p}</Badge>)}</div>
          </div>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto" data-testid="role-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Edit" : "Tambah"} Role</DialogTitle>
            <DialogDescription>Pilih izin yang diberikan untuk role ini.</DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-1.5"><Label>Nama Role</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: Kepala Gudang" data-testid="role-name-input" /></div>
              <div className="space-y-2">
                {perms.map((p) => (
                  <label key={p.key} className="flex items-center gap-3 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-accent">
                    <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={(v) => toggle(p.key, !!v)} data-testid={`perm-${p.key}`} />
                    {p.label}
                  </label>
                ))}
              </div>
              <Button type="submit" className="w-full" data-testid="role-save-btn">Simpan</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus role?" description={`Role "${del?.name}" akan dihapus.`} onConfirm={remove} />
    </div>
  );
};

export default function Team() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const loadRoles = useCallback(() => api.get("/roles").then((r) => setRoles(r.data)), []);
  useEffect(() => { loadRoles(); api.get("/permissions").then((r) => setPerms(r.data)); }, [loadRoles]);
  return (
    <PageWrap>
      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">Pengguna</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Role & Hak Akses</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab roles={roles} /></TabsContent>
        <TabsContent value="roles" className="mt-6"><RolesTab roles={roles} perms={perms} reload={loadRoles} /></TabsContent>
      </Tabs>
    </PageWrap>
  );
}
